import { api } from '../axios'
import type { ApiResponse } from '../../../types'

export const simpleProfileApi = {
  // Profile Pictures
  uploadProfilePicture: (file: File, teacherId: number) => {
    const formData = new FormData()
    formData.append('profilePicture', file)
    return api.post<ApiResponse<{ url: string; file_path?: string }>>(
      `/simple/profile-picture/${teacherId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  },

  getProfilePicture: (teacherId: number) =>
    api.get<ApiResponse<{ url: string; file_path?: string }>>(`/simple/profile-picture/${teacherId}`),

  // Profile Completion + Update Requests
  getMyProfileCompletion: () => api.get<ApiResponse<any>>('/simple/me/completion'),
  getMyProfileUpdateRequest: () => api.get<ApiResponse<any>>('/simple/me/profile-update-request'),
  submitMyProfileUpdateRequest: (payload: Record<string, unknown>) =>
    api.post<ApiResponse<any>>('/simple/me/profile-update-request', payload),
  getMyPhoneChangeRequest: () => api.get<ApiResponse<any>>('/simple/me/phone-change-request'),
  submitMyPhoneChangeRequest: (payload: { newPhone: string; reason: string }) =>
    api.post<ApiResponse<any>>('/simple/me/phone-change-request', payload),
  lookupTeacherByOldPhone: (oldPhone: string, teacherId: string) =>
    api.get<ApiResponse<{ teacherId: string; teacherName: string }>>('/simple/public/phone-change-lookup', {
      params: { oldPhone, teacherId },
    }),
  submitPublicPhoneChangeRequest: (payload: {
    teacherId: string
    teacherName?: string
    oldPhone: string
    newPhone: string
    reason: string
  }) =>
    api.post<ApiResponse<any>>('/simple/public/phone-change-request', payload),

  // Admin: profile update requests
  adminListProfileUpdateRequestsPending: (params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<any>>('/simple/profile-update-requests/admin/pending', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 20 },
    }),
  adminApproveProfileUpdateRequest: (id: number) =>
    api.patch<ApiResponse<any>>(`/simple/profile-update-requests/admin/${id}/approve`),
  adminRejectProfileUpdateRequest: (id: number, remarks: string) =>
    api.patch<ApiResponse<any>>(`/simple/profile-update-requests/admin/${id}/reject`, { remarks }),
  adminReviewProfileUpdateRequestPartial: (id: number, body: { approveKeys?: string[]; rejectNotes?: Record<string, string> }) =>
    api.patch<ApiResponse<any>>(`/simple/profile-update-requests/admin/${id}/review`, body),
  adminBulkApproveProfileUpdateRequests: (ids: number[]) =>
    api.post<ApiResponse<any>>('/simple/profile-update-requests/admin/bulk-approve', { ids }),
  adminPendingPhoneChangeCount: () =>
    api.get<ApiResponse<{ count: number }>>('/simple/phone-change-requests/admin/pending-count'),
  adminListPendingPhoneChanges: (params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<any>>('/simple/phone-change-requests/admin/pending', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 20 },
    }),
  adminListPhoneChangeRequests: (params?: { status?: 'pending' | 'approved' | 'rejected'; page?: number; limit?: number }) =>
    api.get<ApiResponse<any>>('/simple/phone-change-requests/admin/list', {
      params: { status: params?.status ?? 'pending', page: params?.page ?? 1, limit: params?.limit ?? 20 },
    }),
  adminApprovePhoneChangeRequest: (id: number) =>
    api.patch<ApiResponse<any>>(`/simple/phone-change-requests/admin/${id}/approve`),
  adminRejectPhoneChangeRequest: (id: number, remarks: string) =>
    api.patch<ApiResponse<any>>(`/simple/phone-change-requests/admin/${id}/reject`, { remarks }),
  superAdminHardDeletePhoneChangeRequest: (id: number) =>
    api.delete<ApiResponse<any>>(`/simple/phone-change-requests/admin/${id}/hard-delete`),
  superAdminBulkHardDeletePhoneChangeRequests: (ids: number[]) =>
    api.post<ApiResponse<any>>('/simple/phone-change-requests/admin/bulk-hard-delete', { ids }),

  // Admin: profile picture review
  adminListProfilePicturesPending: (params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<any>>('/simple/profile-picture/admin/pending', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 20 },
    }),
  adminVerifyProfilePicture: (id: number) => api.patch<ApiResponse<any>>(`/simple/profile-picture/admin/${id}/verify`),
  adminRejectProfilePicture: (id: number, remarks: string) =>
    api.patch<ApiResponse<any>>(`/simple/profile-picture/admin/${id}/reject`, { remarks }),

  // Education Qualifications
  getQualifications: (teacherId: number) =>
    api.get<ApiResponse<any[]>>(`/simple/qualifications/${teacherId}`),

  createQualification: (data: any) =>
    api.post<ApiResponse<any>>('/simple/qualifications', data),

  updateQualification: (id: number, data: any) =>
    api.put<ApiResponse<any>>(`/simple/qualifications/${id}`, data),

  deleteQualification: (id: number) =>
    api.delete<ApiResponse>(`/simple/qualifications/${id}`),

  verifyQualification: (id: number) =>
    api.patch<ApiResponse<any>>(`/simple/qualifications/${id}/verify`),

  rejectQualification: (id: number, remarks: string) =>
    api.patch<ApiResponse<any>>(`/simple/qualifications/${id}/reject`, { remarks }),

  /** requireAdmin: delete an approved qualification; remarks required */
  deleteVerifiedQualification: (id: number, remarks: string) =>
    api.post<ApiResponse>(`/simple/qualifications/${id}/delete-verified`, { remarks }),

  getAdminEducationQualPendingCount: () =>
    api.get<ApiResponse<{ count: number }>>('/simple/qualifications/admin/pending-count'),

  getAdminEducationQualReviewList: (
    status: 'pending' | 'rejected' | 'verified' | 'all' = 'pending',
    params?: { page?: number; limit?: number }
  ) =>
    api.get<ApiResponse<any[]> & { pagination?: { page: number; limit: number; total: number; totalPages: number } }>(
      `/simple/qualifications/admin/review`,
      {
        params: {
          status,
          page: params?.page ?? 1,
          limit: params?.limit ?? 10,
        },
      }
    ),

  // Document Uploads
  uploadDocument: (file: File, qualificationId: number, type: 'marksheet' | 'certificate') => {
    const formData = new FormData()
    formData.append('document', file)
    formData.append('qualificationId', qualificationId.toString())
    formData.append('type', type)
    formData.append('docType', 'education')
    return api.post<ApiResponse<any>>('/simple/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  // Training Details
  getTrainings: (teacherId: number) =>
    api.get<ApiResponse<any[]>>(`/simple/trainings/${teacherId}`),

  createTraining: (data: any) =>
    api.post<ApiResponse<any>>('/simple/trainings', data),

  updateTraining: (id: number, data: any) =>
    api.put<ApiResponse<any>>(`/simple/trainings/${id}`, data),

  deleteTraining: (id: number) =>
    api.delete<ApiResponse>(`/simple/trainings/${id}`),

  uploadTrainingCertificate: (file: File, trainingId: number) => {
    const formData = new FormData()
    formData.append('document', file)
    formData.append('trainingId', trainingId.toString())
    formData.append('type', 'certificate')
    formData.append('docType', 'training')
    return api.post<ApiResponse<any>>('/simple/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  // MLA Spouse Badges
  getMLABadge: (teacherId: number) =>
    api.get<ApiResponse<any>>(`/mla-badges/teacher/${teacherId}`),

  requestMLABadge: (data: {
    teacherId: number
    mlaName: string
    mlaConstituency?: string
    relationship: 'spouse' | 'husband' | 'wife'
    validFrom?: string
    validUntil?: string
    supportingDocument?: File
  }) => {
    const formData = new FormData()
    formData.append('teacherId', data.teacherId.toString())
    formData.append('mlaName', data.mlaName)
    if (data.mlaConstituency) formData.append('mlaConstituency', data.mlaConstituency)
    formData.append('relationship', data.relationship)
    if (data.validFrom) formData.append('validFrom', data.validFrom)
    if (data.validUntil) formData.append('validUntil', data.validUntil)
    if (data.supportingDocument) formData.append('supportingDocument', data.supportingDocument)

    return api.post<ApiResponse<any>>('/mla-badges/request', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}

